import { ref, shallowRef } from 'vue'
import { currentSession } from '@/api/tokens'
import { getResolvedSellerEntries, type ResolvedSellerEntries } from '@/api/sellerEntry'
import { sellerEntryVisible as pathVisible, type SellerEntryModule } from './sellerEntryScope'

const resolved = shallowRef<ResolvedSellerEntries | null>(null)
export const sellerEntryState = ref<'idle' | 'loading' | 'ready' | 'error'>('idle')
let owner = '', generation = 0, failures = 0
let refresh: ReturnType<typeof setTimeout> | null = null
let expiry: ReturnType<typeof setTimeout> | null = null
let inFlight: Promise<void> | null = null
const ownerNow = () => {
  const s = currentSession()
  return s?.subjectType === 'TENANT' ? JSON.stringify([s.subjectType, s.tenantId, s.userId, s.subjectId]) : ''
}
const owned = () => owner !== '' && owner === ownerNow()
const fresh = () => owned() && sellerEntryState.value === 'ready'
  && !!resolved.value && Date.parse(resolved.value.expiresAt) > Date.now()
export function sellerModuleVisible(module: SellerEntryModule): boolean {
  return fresh() && !!resolved.value?.visibleModules.includes(module)
}
export function sellerEntryVisible(path: string): boolean {
  return pathVisible(path, fresh() ? resolved.value?.visibleModules ?? [] : [])
}
/** Last verified menu structure only; never used to authorize navigation or writes. */
export function sellerEntryPresented(path: string): boolean {
  return pathVisible(path, owned() ? resolved.value?.visibleModules ?? [] : [])
}
export function sellerModulePresented(module: SellerEntryModule): boolean {
  return owned() && !!resolved.value?.visibleModules.includes(module)
}
export function sellerEntryLink(path: string) {
  const enabled = sellerEntryVisible(path)
  return { 'data-seller-entry': path, 'aria-disabled': !enabled, tabindex: enabled ? undefined : -1,
    title: enabled ? undefined : '功能入口正在核验，稍后将自动恢复' }
}
export function blockUnverifiedSellerEntry(event: Event): boolean {
  const anchor = event.target instanceof Element ? event.target.closest('[data-seller-entry]') : null
  const path = anchor?.getAttribute('data-seller-entry')
  if (!path || sellerEntryVisible(path)) return false
  event.preventDefault(); event.stopImmediatePropagation()
  return true
}
export function resetSellerEntryVisibility(): void {
  ++generation
  if (refresh) clearTimeout(refresh)
  if (expiry) clearTimeout(expiry)
  refresh = expiry = null; inFlight = null; failures = 0
  owner = ''; resolved.value = null; sellerEntryState.value = 'idle'
}
/** Refreshes retain still-valid resolution; expired presentation never extends the server lease. */
export function loadSellerEntryVisibility(): Promise<void> {
  const key = ownerNow()
  if (!key) { resetSellerEntryVisibility(); return Promise.resolve() }
  if (key !== owner) { resetSellerEntryVisibility(); owner = key }
  if (inFlight) return inFlight
  if (refresh) clearTimeout(refresh)
  refresh = null
  const run = generation, previousDeadline = resolved.value?.expiresAt
  if (!fresh()) sellerEntryState.value = 'loading'
  const current = () => run === generation && key === ownerNow()
  const promise = Promise.resolve().then(async () => {
    try {
      const value = await getResolvedSellerEntries()
      if (!current()) return
      const remaining = Date.parse(value.expiresAt) - Date.now()
      if (!Number.isFinite(remaining) || remaining <= 0 || remaining > value.maxStaleSeconds * 1000 + 1000) throw new Error('入口配置已过期')
      resolved.value = value; failures = 0; sellerEntryState.value = 'ready'
      if (expiry) clearTimeout(expiry)
      expiry = setTimeout(() => {
        if (!current()) return
        sellerEntryState.value = 'loading'
        void loadSellerEntryVisibility()
      }, remaining)
      // A cached response with the same deadline must not cause a tight early-refresh loop.
      const delay = value.expiresAt === previousDeadline ? remaining : remaining - Math.min(5000, remaining / 5)
      refresh = setTimeout(() => { if (current()) void loadSellerEntryVisibility() }, delay)
    } catch {
      if (!current()) return
      if (!fresh()) sellerEntryState.value = 'error'
      const delay = [2000, 5000, 10000, 20000, 30000][Math.min(failures++, 4)]!
      refresh = setTimeout(() => { if (current()) void loadSellerEntryVisibility() }, delay)
    }
  })
  inFlight = promise
  void promise.then(() => { if (inFlight === promise) inFlight = null })
  return promise
}
